import { addPromptToGamePack, createPrompt } from '@/client/gamePacks';
import { GamePack } from '@/models/GamePack.model';
import { listAllGamePacks } from '@/utils/gamePacks';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { PromptDialogForm } from './PromptDialogForm';

interface CreatePromptDialogProps {
  /** When present, prompt will be created and assigned to game pack */
  gamePackId?: string;

  isOpen: boolean;

  setIsOpen: (isOpen: boolean) => void;
  onSubmit?: () => void;
}

export type SelectedGamePack = Partial<GamePack> & {
  selected: boolean;
};

export type PromptInputs = {
  prompt: string;
  gamePacks: SelectedGamePack[];
};

export const CreatePromptDialog = ({
  setIsOpen,
  onSubmit,
  isOpen,
  gamePackId
}: CreatePromptDialogProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<PromptInputs>({
    mode: 'onSubmit',
    defaultValues: {
      gamePacks: []
    }
  });
  const { fields, replace: replaceGamePacks } = useFieldArray({
    control,
    name: 'gamePacks'
  });

  const onCreate = async (data: PromptInputs) => {
    if (data.prompt) {
      try {
        setIsSubmitting(true);
        const selectedGamePacks = data.gamePacks.filter((gamePack) => gamePack.selected === true);

        const result = await createPrompt(data.prompt);
        const promptId = result.data.promptCreate?.id;

        if (!promptId) {
          throw new Error('Could not create prompt');
        }

        if (selectedGamePacks.length > 0) {
          const promises = selectedGamePacks
            .filter((gamePack) => gamePack.id)
            .map((gamePack) => addPromptToGamePack(gamePack.id as string, promptId));

          await Promise.all(promises);
        }

        setIsSubmitting(false);
        reset();
        setIsOpen(false);
        if (onSubmit) {
          onSubmit();
        }
      } catch (error) {
        setIsSubmitting(false);
        console.error({ error });
        throw new Error('Could not create prompt or assign to GamePacks');
      }
    }
  };

  useEffect(() => {
    if (isOpen === true) {
      reset();
      setIsLoading(true);
      listAllGamePacks()
        .then((result) => {
          const _gamePacks = result.data?.gamePackList
            .map((item) => {
              if (item) {
                return {
                  id: item.id as string,
                  name: item.name as string,
                  selected: item.id === gamePackId ? true : false
                } as SelectedGamePack;
              }
              return undefined;
            })
            .filter((item: any) => item !== undefined) as SelectedGamePack[];
          replaceGamePacks(_gamePacks ?? []);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
      <DialogTitle className="text-[24px] pb-[0px] font-bold">Create Prompt</DialogTitle>
      <PromptDialogForm
        register={register}
        fields={fields}
        errors={errors}
        onCancel={() => {
          reset();
          setIsOpen(false);
        }}
        onSubmit={handleSubmit(onCreate)}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
      />
    </Dialog>
  );
};
